import { useState } from 'react';

interface ChatCommandSettingsProps {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  prefix: string;
  onPrefixChange: (prefix: string) => void;
  modOnly: boolean;
  onModOnlyChange: (modOnly: boolean) => void;
  cooldown: number;
  onCooldownChange: (cooldown: number) => void;
  sendCooldownMessages: boolean;
  onSendCooldownMessagesChange: (send: boolean) => void;
}

const SPEED_PRESETS = [
  { label: 'Fast', seconds: 5, helper: 'More viewer interactions' },
  { label: 'Balanced', seconds: 10, helper: 'Recommended' },
  { label: 'Calm', seconds: 20, helper: 'Less chat spam' },
];

export function ChatCommandSettings({
  enabled,
  onEnabledChange,
  prefix,
  onPrefixChange,
  modOnly,
  onModOnlyChange,
  cooldown,
  onCooldownChange,
  sendCooldownMessages,
  onSendCooldownMessagesChange,
}: ChatCommandSettingsProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const activePreset = SPEED_PRESETS.find((preset) => preset.seconds === cooldown);

  return (
    <div className="space-y-4">
      <div className="relative overflow-hidden rounded-[28px] border border-cyan-300/[0.14] bg-[linear-gradient(135deg,rgba(15,23,42,0.82),rgba(10,17,42,0.76)_52%,rgba(41,18,58,0.70))] p-4 shadow-[0_20px_70px_rgba(0,0,0,0.24)]">
        <div className="pointer-events-none absolute -right-16 -top-20 h-52 w-52 rounded-full bg-fuchsia-500/14 blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 left-1/3 h-24 w-64 bg-cyan-400/10 blur-3xl" />
        <div className="relative flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="mb-3 inline-flex rounded-full border border-fuchsia-300/20 bg-fuchsia-400/10 px-3 py-1 text-[10px] font-black uppercase tracking-[0.22em] text-fuchsia-100">
              Viewer commands
            </div>
            <h3 className="text-2xl font-black text-white">Let chat talk to StreamBuddy</h3>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300/72">
              Viewers can use your command to ask StreamBuddy for a spoken reply during stream.
            </p>
          </div>
          <div className="grid gap-2 sm:grid-cols-3 lg:min-w-[360px]">
            <div className="rounded-2xl border border-white/10 bg-black/22 px-3 py-3">
              <div className="text-[9px] font-black uppercase tracking-widest text-cyan-200/65">Status</div>
              <div className={`mt-1 text-sm font-black ${enabled ? 'text-emerald-300' : 'text-slate-400'}`}>{enabled ? 'On' : 'Off'}</div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-black/22 px-3 py-3">
              <div className="text-[9px] font-black uppercase tracking-widest text-cyan-200/65">Command</div>
              <div className="mt-1 truncate font-mono text-sm font-black text-cyan-100">{prefix || '!buddy'}</div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-black/22 px-3 py-3">
              <div className="text-[9px] font-black uppercase tracking-widest text-cyan-200/65">Pace</div>
              <div className="mt-1 text-sm font-black text-white">{activePreset?.label ?? `${cooldown}s`}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-4 rounded-[24px] border border-white/10 bg-black/20 p-4">
        <div>
          <span className="text-sm font-black text-white">Let viewers use chat commands</span>
          <p className="mt-1 text-xs text-slate-400">When this is on, chat can trigger StreamBuddy with your command word.</p>
        </div>
        <button
          onClick={() => onEnabledChange(!enabled)}
          className={`relative h-8 w-14 shrink-0 rounded-full transition-colors ${enabled ? 'bg-cyan-500 shadow-lg shadow-cyan-500/20' : 'bg-white/15'}`}
        >
          <div className={`absolute top-1 h-6 w-6 rounded-full bg-white transition-transform ${enabled ? 'translate-x-7' : 'translate-x-1'}`} />
        </button>
      </div>

      {enabled && (
        <>
          <div className="grid gap-4 xl:grid-cols-[minmax(0,1.1fr)_minmax(300px,0.9fr)]">
            <div className="rounded-[24px] border border-cyan-300/[0.12] bg-cyan-400/[0.045] p-4">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-black text-white">Command word</p>
                  <p className="mt-1 text-xs text-slate-400">What viewers type before their message.</p>
                </div>
                <input
                  type="text"
                  value={prefix}
                  onChange={(e) => onPrefixChange(e.target.value)}
                  className="w-full rounded-2xl border border-cyan-300/25 bg-black/35 px-4 py-3 text-center font-mono text-sm font-black text-cyan-100 outline-none transition-colors focus:border-cyan-300/70 sm:w-36"
                  placeholder="!buddy"
                />
              </div>
              <div className="mt-4 rounded-2xl border border-white/10 bg-black/20 px-3 py-3 text-xs leading-5 text-cyan-100/65">
                Example: <span className="font-mono font-black text-cyan-100">{prefix || '!buddy'}</span> hype up chat after that clutch.
              </div>
            </div>

            <div className="rounded-[24px] border border-fuchsia-300/[0.12] bg-fuchsia-400/[0.045] p-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-black text-white">Only moderators can use it</p>
                  <p className="mt-1 text-xs text-slate-400">Turn this off when you want the whole chat to use the command.</p>
                </div>
                <button
                  onClick={() => onModOnlyChange(!modOnly)}
                  className={`relative h-8 w-14 shrink-0 rounded-full transition-colors ${modOnly ? 'bg-fuchsia-500 shadow-lg shadow-fuchsia-500/20' : 'bg-white/15'}`}
                >
                  <div className={`absolute top-1 h-6 w-6 rounded-full bg-white transition-transform ${modOnly ? 'translate-x-7' : 'translate-x-1'}`} />
                </button>
              </div>
              <div className="mt-4 rounded-2xl border border-white/10 bg-black/20 px-3 py-3">
                <div className="text-[9px] font-black uppercase tracking-widest text-fuchsia-200/65">Current access</div>
                <div className="mt-1 text-sm font-black text-fuchsia-100">{modOnly ? 'Moderators only' : 'Everyone in chat'}</div>
              </div>
            </div>
          </div>

          <div className="space-y-3 rounded-[24px] border border-white/10 bg-black/20 p-4">
            <div>
              <p className="text-sm font-black text-white">How often one viewer can use it</p>
              <p className="mt-1 text-xs text-slate-400">This keeps one person from making StreamBuddy talk nonstop.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {SPEED_PRESETS.map((preset) => (
                <button
                  key={preset.label}
                  onClick={() => onCooldownChange(preset.seconds)}
                  className={`rounded-2xl border p-3 text-left transition-all ${cooldown === preset.seconds
                    ? 'border-cyan-300/55 bg-[linear-gradient(135deg,rgba(34,211,238,0.16),rgba(168,85,247,0.14))] text-white shadow-[0_0_24px_rgba(34,211,238,0.12)]'
                    : 'border-white/10 bg-white/[0.035] text-slate-300 hover:border-cyan-300/20 hover:bg-cyan-300/[0.055]'
                    }`}
                >
                  <p className="text-xs font-black">{preset.label}</p>
                  <p className="mt-1 text-[11px] opacity-80">{preset.seconds}s - {preset.helper}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-hidden rounded-[24px] border border-white/10 bg-black/20">
            <button
              onClick={() => setShowAdvanced((prev) => !prev)}
              className="flex w-full items-center justify-between p-4 text-left transition-colors hover:bg-white/[0.03]"
            >
              <div>
                <p className="text-sm font-black text-white">Fine tuning</p>
                <p className="mt-1 text-xs text-slate-400">Set exact seconds and decide whether chat sees cooldown messages.</p>
              </div>
              <span className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[10px] font-black uppercase tracking-widest text-cyan-100/70">
                {showAdvanced ? 'Hide' : 'Show'}
              </span>
            </button>

            {showAdvanced && (
              <div className="space-y-4 border-t border-white/10 p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-black text-white">Exact cooldown</p>
                    <p className="mt-1 text-xs text-slate-400">Set the number of seconds each viewer must wait.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="0"
                      max="300"
                      value={cooldown}
                      onChange={(e) => onCooldownChange(parseInt(e.target.value, 10) || 0)}
                      className="w-24 rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-center font-mono text-sm text-white outline-none focus:border-cyan-400/60"
                    />
                    <span className="text-sm text-slate-500">sec</span>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/[0.025] px-3 py-3">
                  <div>
                    <p className="text-sm font-black text-white">Tell chat when they are on cooldown</p>
                    <p className="mt-1 text-xs text-slate-400">StreamBuddy can send a short wait message when someone tries too soon.</p>
                  </div>
                  <button
                    onClick={() => onSendCooldownMessagesChange(!sendCooldownMessages)}
                    className={`relative h-8 w-14 shrink-0 rounded-full transition-colors ${sendCooldownMessages ? 'bg-cyan-500 shadow-lg shadow-cyan-500/20' : 'bg-white/15'}`}
                  >
                    <div className={`absolute top-1 h-6 w-6 rounded-full bg-white transition-transform ${sendCooldownMessages ? 'translate-x-7' : 'translate-x-1'}`} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {!enabled && (
        <div className="rounded-[24px] border border-dashed border-white/10 bg-white/[0.025] p-8 text-center">
          <p className="text-sm font-black text-white">Commands are off</p>
          <p className="mt-2 text-xs text-slate-500">Turn them on when you want chat to trigger StreamBuddy directly.</p>
        </div>
      )}
    </div>
  );
}
